// @workflow assistant.attachments
/**
 * Every word attachments show or say, in one place (the attachments showcase,
 * src/assets/showcase/ai-chat-client-attachments-showcase.html). A rejected file shows the
 * server's own `reason`; a refusal code is worded here. Sizes and kinds come from the server's
 * limits, never written in.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { ATTACHMENT_CODE as CODE, CHIP_STATUS } from './attachments.js';
import { sizeLabel } from './files.js';

/** The showcase's kind words (badge and the "kind · size" line). */
export const KIND_WORD = Object.freeze({ csv: 'CSV', xlsx: 'Excel', pdf: 'PDF', docx: 'Word', pptx: 'PowerPoint', text: 'Text', image: 'Image' });
export const KIND_BADGE = Object.freeze({ csv: 'CSV', xlsx: 'XLSX', pdf: 'PDF', image: 'IMG', docx: 'DOCX', pptx: 'PPTX', text: 'TXT' });

const kindWord = (kind) => KIND_WORD[kind] || (kind ? String(kind).toUpperCase() : 'File');
export const kindBadge = (kind) => KIND_BADGE[kind] || (kind ? String(kind).slice(0, 4).toUpperCase() : '');

/** ['csv', 'xlsx'] → "CSV or Excel". */
export function acceptWords(kinds) {
  const list = (kinds || []).map(kindWord);
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} or ${list[list.length - 1]}`;
}

export const ATTACH_TEXT = Object.freeze({
  ATTACH: 'Attach a file',
  OFF_ANSWERING: 'Files are off while you answer',
  UNAVAILABLE: "Attaching files isn't available right now",
  /** The server's file storage is down (limits `available: false`, or an upload refused as attachment.unavailable). */
  STORAGE_DOWN: "File storage isn't available right now. Try again in a minute.",
  INPUT_LABEL: 'Choose files to attach',
  LIST_LABEL: 'Attached files',
  SENT_LIST_LABEL: 'Files sent with this message',
  RETRY: 'Retry',
  WAIT: 'Wait for the upload to finish',
  DROP: 'Drop files to attach',
  /** The box's placeholder while files are attached: a message needs text (messageBody.text). */
  PLACEHOLDER: 'Say what to do with the file',
  UNAVAILABLE_FILE: 'No longer available',
});

const ERRORS = Object.freeze({
  [CODE.TOO_LARGE]: ({ maxBytes }) => `The file is larger than ${sizeLabel(maxBytes)}`,
  [CODE.UNSUPPORTED_TYPE]: ({ kinds }) => `This file type can't be read. Attach a ${acceptWords(kinds) || 'supported'} file.`,
  [CODE.EMPTY]: () => 'The file is empty',
  [CODE.UNREADABLE]: () => "AVA couldn't read this file. Check that it opens, then attach it again.",
  [CODE.UNAVAILABLE]: () => ATTACH_TEXT.STORAGE_DOWN,
  [CODE.FAILED]: () => 'Upload failed. Nothing was sent.',
  [CODE.NOT_FOUND]: () => 'A file in this message is no longer available. Remove it or attach it again, then send.',
  [CODE.TOO_MANY]: () => 'Too many files for one message. Remove one, then send again.',
});

/** A code's words; an unknown code reads as a failed upload. */
export function attachmentError(code, limits) {
  return (ERRORS[code] || ERRORS[CODE.FAILED])(limits || {});
}

/** The chip's second line: the percentage, the reason, or "kind · size". */
export function chipMeta(chip, limits) {
  if (chip.status === CHIP_STATUS.UPLOADING) return `Uploading · ${Math.round((chip.progress || 0) * 100)}%`;
  if (chip.status === CHIP_STATUS.ERROR) return chip.reason || attachmentError(chip.code, limits);
  const size = sizeLabel(chip.size);
  return size ? `${kindWord(chip.kind)} · ${size}` : kindWord(chip.kind);
}

/** The paperclip's name: why it is off, else what it takes. */
export function attachLabel({ limitsFailed = false, storageDown = false, answering = false, full = false, limits = null }) {
  if (limitsFailed) return ATTACH_TEXT.UNAVAILABLE;
  if (answering) return ATTACH_TEXT.OFF_ANSWERING;
  if (storageDown) return ATTACH_TEXT.STORAGE_DOWN;
  if (full && limits) return `${limits.maxPerMessage} files a message`;
  if (!limits) return ATTACH_TEXT.ATTACH;
  return `Attach a ${acceptWords(limits.kinds)} file (or drop it on the box)`;
}

/** "a.csv was not added: 5 files a message." */
export const notAddedText = (names, limits) => `${names.join(', ')} ${names.length === 1 ? 'was' : 'were'} not added: ${limits.maxPerMessage} files a message.`;

/** What the composer's live region says. */
export const ATTACH_SAY = Object.freeze({
  ready: (name) => `${name} is attached.`,
  error: (name, why) => `${name} wasn't attached. ${why}`,
  removed: (name) => `Removed ${name}.`,
});
